<template>
    <v-card :loading="loading">
        <v-card-title class="text-h5">
            {{ 'Update External User' }}
            <v-spacer />
            <v-btn
                rounded
                :disabled="!valid || Object.keys(userDiff) == 0 || loading2"
                color="primary"
                class="mx-2"
                :loading="loading2"
                @click="saveExternalUser"
            >
                SAVE
            </v-btn>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-form ref="form" v-model="valid" class="mb-2">
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="userToEdit.name"
                            :rules="[rules.required]"
                            prepend-icon="mdi-account-tie"
                            :disabled="loading2 || loading"
                            label="Name *"
                            required
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="userToEdit.siaId"
                            prepend-icon="mdi-card-account-details"
                            label="SIA ID *"
                            readonly
                            disabled
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6" class="pt-0">
                        <v-text-field
                            v-model="userToEdit.email"
                            :rules="[rules.email]"
                            :disabled="loading2 || loading"
                            prepend-icon="mdi-email"
                            label="Email"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" class="pt-0 mt-3">
                        <vue-tel-input
                            v-model="userToEdit.phone"
                            mode="international"
                            :disabled="loading2 || loading"
                            :inputOptions="inputOptions"
                            validCharactersOnly
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6" class="pt-0 mt-0">
                        <v-text-field
                            v-model="userToEdit.hourlyPrice"
                            :disabled="loading2 || loading"
                            :rules="[rules.required, rules.validDollarAmount]"
                            prepend-icon="mdi-currency-usd"
                            label="Hourly Price *"
                            required
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pt-0" cols="12" sm="6">
                        <v-select
                            :items="languages"
                            v-model="userToEdit.language"
                            :disabled="loading2 || loading"
                            prepend-icon="mdi-account-tie-voice"
                            label="Language"
                        />
                    </v-col>
                    <v-col class="pt-0" cols="12" sm="6">
                        <v-text-field
                            v-model="userToEdit.notes"
                            hide-details
                            :disabled="loading2 || loading"
                            prepend-icon="mdi-note-multiple"
                            label="Notes"
                        />
                    </v-col>
                </v-row>
                <v-switch
                    v-model="userToEdit.active"
                    label="Active"
                    hide-details
                    :disabled="loading2 || loading"
                    :input-value="true"
                    class="my-2"
                ></v-switch>
                <v-switch
                    v-model="userToEdit.subcontracted"
                    label="Subcontracted"
                    hide-details
                    :disabled="loading2 || loading"
                    :input-value="false"
                    class="my-2"
                ></v-switch>
            </v-form>

            <small>* indicates required field</small>
        </v-card-text>
    </v-card>
</template>

<script>
import API from '@/services/api'
import _ from 'lodash'
import { mapMutations } from 'vuex'
import { objDiff } from '@/helpers/objDiff.js'
export default {
    name: 'ExternalUserForm',
    props: {
        id: {
            type: String,
            default: null,
        },
        users: {
            type: Array,
            default() {
                return []
            },
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            languages: ['English', 'Spanish'],
            inputOptions: {
                placeholder: 'Phone',
            },
            company: JSON.parse(localStorage.getItem('company')),
            valid: false,
            rules: {
                required: v => !!v || 'Required',
                email: v => !v || /.+@.+\..+/.test(v) || 'Not a valid E-mail',
                number: () => this.validNumber || 'Not a valid number',
                validDollarAmount: value =>
                    /^\d+(\.\d{1,2})?$/.test(value) ||
                    'Enter a valid amount (maximum 2 decimals)',
                city: v => /^[A-Za-z ]+$/.test(v) || 'Not a valid input',
            },
            originalUser: {},
            loading2: false,
            editUser: JSON.parse(localStorage.getItem('userId')),
            userToEdit: {},
        }
    },
    computed: {
        userDiff: function() {
            const diff = objDiff(this.originalUser, this.userToEdit)
            if (diff.phone === '') {
                delete diff.phone
            }
            return diff
        },
    },
    async mounted() {
        if (this.id) {
            const user = this.users.find(u => u.id === this.id)
            this.originalUser = _.cloneDeep(user)
            this.userToEdit = _.cloneDeep(user)
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async saveExternalUser() {
            try {
                this.loading2 = true
                if (this.userToEdit.phone) {
                    this.userToEdit.phone = this.userToEdit.phone.replace(
                        / /g,
                        ''
                    )
                }
                const paramsToUpdate = { ...this.userDiff }
                paramsToUpdate.updatedBy = this.editUser
                await API.updateGeneralExternalUser({
                    userId: this.userToEdit.id,
                    ...paramsToUpdate,
                })
                this.$root.$emit('refresh-externalUsers')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading2 = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.vue-tel-input {
    border: none;
    border-radius: 0px;
    font-size: 16px;
}
.vue-tel-input.disabled {
    opacity: 0.5;
}

.vue-tel-input:focus-within {
    box-shadow: none;
    border-color: 'primary';
    color: 'primary';
}
</style>
